import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { login } from '@/store/slices/authSlice';
import { parseJWT } from '@/utils';
import { User, UserRole } from '@/types';

// ---------------- Schema ----------------
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

type LoginResponse = {
  accessToken: string;
  expiresIn: number;
  type: string;
  isNewUser: boolean;
};

// ---------------- Helper ----------------
function buildUserFromLoginResponse(result: LoginResponse, email: string): User {
  const payload = parseJWT(result.accessToken) as {
    sub?: string | number;
    email?: string;
    type?: string;
    role?: string;
    iat?: number;
  } | null;

  const resolvedEmail = payload?.email || email;
  const roleValue = payload?.role || payload?.type || result.type;

  const normalizedRole =
    roleValue === UserRole.ADMIN || roleValue === UserRole.MODERATOR ? roleValue : UserRole.USER;

  const displayName = resolvedEmail.split('@')[0] || 'User';

  const timestamp = payload?.iat
    ? new Date(payload.iat * 1000).toISOString()
    : new Date().toISOString();

  return {
    id: String(payload?.sub || resolvedEmail),
    email: resolvedEmail,
    name: displayName,
    role: normalizedRole,
    createdAt: timestamp,
    updatedAt: timestamp,
    isActive: true,
  };
}

// ---------------- Component ----------------
export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isTouched, setIsTouched] = useState(false);

  const [loginMutation, { isLoading }] = useLoginMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/analytics';

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false,
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');

  useEffect(() => {
    if (apiError) {
      setApiError(null);
      clearErrors('password');
    }
  }, [emailValue, passwordValue]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setApiError(null);

      const result = await loginMutation(data).unwrap();

      if (result.type !== UserRole.ADMIN) {
        const message = 'You are not authorized to access the admin panel';

        setApiError(message);

        setError('password', {
          type: 'manual',
          message,
        });

        return;
      }
      const user = buildUserFromLoginResponse(result, data.email);

      dispatch(
        login({
          user,
          tokens: {
            accessToken: result.accessToken,
            refreshToken: null,
          },
        })
      );

      navigate(from, { replace: true });
    } catch (err: any) {
      const message = err?.data?.message || 'Invalid email or password';

      setApiError(message);

      setError('password', {
        type: 'server',
        message,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-center">
        <img src="/logo.svg" alt="Logo" />
      </div>
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-white">
          Sign in to your account
        </h1>
        <p className="text-sm text-muted-foreground">
          Enter your email and password to access your account
        </p>
      </div>

      {/* Backend Error */}
      {/* {apiError && <ErrorBanner type="error" title="Login Failed" message={apiError} />} */}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2 text-white">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            {...register('email')}
            onChange={(e) => {
              setIsTouched(true);
              register('email').onChange(e);
            }}
            className={errors.email && isTouched ? 'border-destructive' : ''}
          />

          {isTouched && errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2 text-white">
          <Label htmlFor="password">Password</Label>

          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              {...register('password')}
              onChange={(e) => {
                setIsTouched(true);
                register('password').onChange(e);
              }}
              className={errors.password && isTouched ? 'border-destructive pr-10' : 'pr-10'}
            />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-black" />
              ) : (
                <Eye className="h-4 w-4 text-black" />
              )}
            </Button>
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign In
        </Button>

        {isTouched && errors.password && (
          <p className="text-sm text-destructive">{errors.password.message}</p>
        )}
      </form>
    </div>
  );
}
